<template>
  <div class="address-block">
    <Text
      v-if="title"
      pattern="body3"
      :weight="500"
      height="150%"
      color="#7a7a7a"
    >
      {{ title }}
    </Text>
    <div
      v-if="isAddress"
      class="address-block_address_row"
      :class="{ 'no-title': !title }"
    >
      <router-link
        v-if="address && network"
        :to="{
          name: 'Address Details',
          params: { network: network, id: address },
        }"
        class="address-block_address underline hover:text-color-6 transition-colors cursor-pointer link"
      >
        {{ shorten(address) }}
      </router-link>
      <span v-else class="address-block_address">
        {{ address ? shorten(address) : "N/A" }}
      </span>
      <BaseSvgIcon
        name="copy"
        class="address-block_copy"
        @click="onCopyAddress(address)"
      />
    </div>
    <BaseSkeleton
      v-if="!isAddress"
      class="w-[100%] h-[16px] rounded-md mt-1"
      isNoAnimation
    />
    <template v-if="hasAmountData">
      <div class="address-block_amount_main">
        <Text pattern="body3" :weight="500" height="150%" color="#7a7a7a">
          {{ $t("Amount") }}
        </Text>
        <div class="address-block_amount_row">
          <div class="address-block_amount" :class="amountClass">
            <BaseSvgIcon name="confirmed" />
            <span class="address-block_amount_title">
              {{ props.amountTransferred.amount }}
              {{ displaySymbol }}
            </span>
            <span v-if="formattedUsdValue" class="address-block_amount_title">
              &nbsp;•&nbsp; {{ formattedUsdValue }}
            </span>
          </div>
          <BaseSvgIcon
            v-if="showArrow"
            name="circle_arrow_left"
            :class="{
              reverse: direction === 'out',
            }"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import { Text } from "@/shared/ui";
import {
  shorten,
  // first36symbols,
  // first25symbols,
  // amount as formatBalance,
} from "@/shared/utils/format";
import { copyToClipboard } from "@emcd/common/helpers";
import BaseSvgIcon from "@/shared/ui/BaseSvgIcon/BaseSvgIcon.vue";
import BaseSkeleton from "@/shared/ui/BaseSkeleton/BaseSkeleton.vue";
import "@/assets/icons/SVG/copy.svg";
import "@/assets/icons/SVG/circle_arrow_left.svg";
import "@/assets/icons/SVG/confirmed.svg";
import { formatFiat } from "@/shared/utils/formaters/transactionFormatter";

// Props
const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  address: {
    type: String,
    required: true,
  },
  isAddress: {
    type: Boolean,
    default: true,
  },
  amountTransferred: {
    type: Object,
    default: null,
    validator: (value) => {
      if (value === null) return true; // Allow null
      return (
        typeof value === "object" &&
        value !== null &&
        "amount" in value &&
        "decimals" in value &&
        "symbol" in value &&
        "usd_value" in value
      );
    },
  },
  direction: {
    type: String,
    default: "in",
    validator: (value) => ["in", "out", "self"].includes(value),
  },
  network: {
    type: String,
    required: true,
  },
  showArrow: {
    type: Boolean,
    default: true,
  },
  showAmountSection: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const { proxy } = getCurrentInstance();

// Methods
const onCopyAddress = (address) => {
  copyToClipboard(address, () => {});
  proxy.$toast({
    type: "success",
    title: t("AddressCopied"),
  });
};

// Computed properties
const hasAmountData = computed(() => {
  return props.amountTransferred && props.showAmountSection;
});

// const formattedAmount = computed(() => {
//   if (!hasAmountData.value) return "";
//   return formatBalance(
//     props.amountTransferred.amount,
//     props.amountTransferred.decimals,
//   );
// });

const formattedUsdValue = computed(() => {
  if (
    !hasAmountData.value ||
    props.amountTransferred.usd_value === null ||
    props.amountTransferred.usd_value === undefined
  )
    return "";
  return formatFiat(props.amountTransferred.usd_value);
});

const displaySymbol = computed(() => {
  return hasAmountData.value ? props.amountTransferred.symbol : "";
});

const amountClass = computed(() => {
  if (!hasAmountData.value) return "";
  if (props.direction === "out") return "sent";
  if (props.direction === "in") return "received";
  return "neutral";
});
</script>

<style lang="scss" scoped>
.address-block {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid var(--Dark-850, #1a1a1a);
  padding: 20px 24px;

  &_address_row:not(.no-title) {
    margin-top: 4px;
  }

  &_address_row:has(+ .address-block_amount_row:not(:last-child)) {
    margin-bottom: 15px;
  }

  &_address_row:not(:has(+ .address-block_amount_row)) {
    margin-bottom: 0;
  }

  &_address {
    // width: 100%;
    overflow: hidden;
    color: #fff;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 130%;

    @media (max-width: 1024px) {
      max-width: 220px;
    }

    @media (max-width: 768px) {
      max-width: 100%;
    }

    &_row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
  }

  &_amount_main {
    margin-top: 16px;
  }

  &_amount {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    color: #fafafa;
    display: flex;
    align-items: center;
    gap: 4px;

    &.neutral {
      color: #fafafa;
    }

    &_row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &_title {
      line-height: 14px;
    }
  }

  &_copy {
    transition: color 0.3s;
    cursor: pointer;
    filter: brightness(0) saturate(100%) invert(76%) sepia(48%) saturate(5896%)
      hue-rotate(220deg) brightness(104%) contrast(98%);

    &:hover {
      filter: brightness(0) saturate(100%) invert(31%) sepia(96%)
        saturate(3271%) hue-rotate(251deg) brightness(100%) contrast(103%);
    }
  }

  .reverse {
    transform: rotate(180deg);
  }
}
</style>
